// src/pages/tenant/TenantDataExportPage.tsx
// Request data exports (GDPR) and view export history.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useTenant, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  Alert,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
} from '@react/components';
import { ExportTenantDialog, toDataExportFormatEnum, type ExportFormat, type ExportScope } from '@/features/tenant/ExportTenantDialog';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Format byte count to human-readable string. */
function formatBytes(bytes: number | undefined): string {
  if (!bytes || bytes === 0) return '--';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / Math.pow(1024, i);
  return `${value % 1 === 0 ? value : value.toFixed(1)} ${units[i]}`;
}

/** Format ISO date to localized string. */
function formatDate(iso: string | undefined): string {
  if (!iso) return '--';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/** Truncate an ID string for display. */
function truncateId(id: string | undefined): string {
  if (!id) return '--';
  if (id.length <= 12) return id;
  return `${id.slice(0, 8)}...${id.slice(-4)}`;
}

/** Map export status to Badge variant. */
function statusVariant(status: string): 'warning' | 'default' | 'success' | 'error' | 'secondary' {
  switch (status) {
    case 'PENDING':
      return 'warning';
    case 'PROCESSING':
      return 'default';
    case 'COMPLETED':
      return 'success';
    case 'FAILED':
      return 'error';
    case 'EXPIRED':
      return 'secondary';
    default:
      return 'secondary';
  }
}

/** Human-readable status label. */
function statusLabel(status: string, t: (k: string) => string): string {
  switch (status) {
    case 'PENDING':
      return t('tenant.export.statusPending');
    case 'PROCESSING':
      return t('tenant.export.statusProcessing');
    case 'COMPLETED':
      return t('tenant.export.statusCompleted');
    case 'FAILED':
      return t('tenant.export.statusFailed');
    case 'EXPIRED':
      return t('tenant.export.statusExpired');
    default:
      return status;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * TenantDataExportPage - request data exports and view export history.
 * Route: /tenants/:id/settings/export
 */
export const TenantDataExportPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useTenant();
  const { t } = useI18n();

  const [showExport, setShowExport] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState(false);

  const [exports, setExports] = useState<any[]>([]);
  const tenant = store.currentItem;
  const tenantName = tenant?.name ?? tenant?.slug ?? id ?? '';

  const loadExports = (tenantId: string) => {
    store.ListTenantDataExports(tenantId).then((res: any) => {
      const items = res?.exports?.items;
      setExports(Array.isArray(items) ? items : []);
    }).catch(() => {});
  };

  useEffect(() => {
    if (id) {
      loadExports(id);
    }
  }, [id]);

  const handleRequestExport = async (format: ExportFormat, scopes: ExportScope[]) => {
    if (!id) return;
    await store.ExportTenantData(id, { format: toDataExportFormatEnum(format) as never, scopes });
    setRequestSuccess(true);
    setTimeout(() => setRequestSuccess(false), 5000);
    // Refresh export list
    loadExports(id);
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="text-sm">
        <Link to="/tenants" className="text-muted-foreground hover:text-foreground">
          {t('tenant.list')}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to={`/tenants/${id}`} className="text-muted-foreground hover:text-foreground">
          {tenantName}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to={`/tenants/${id}/settings`} className="text-muted-foreground hover:text-foreground">
          {t('tenant.settings')}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{t('tenant.export.title')}</span>
      </nav>

      {/* The tab already names this page; the heading stays for screen readers. */}
      <h1 className="sr-only">{t('tenant.export.title')}</h1>

      {/* Success */}
      {requestSuccess && (
        <Alert variant="success" dismissible onDismiss={() => setRequestSuccess(false)}>
          {t('tenant.export.requestSuccess')}
        </Alert>
      )}

      {/* Error */}
      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Request New Export — trigger dialog (docs/ux/14-forms.md) */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div>
            <CardTitle>{t('tenant.export.requestTitle')}</CardTitle>
            <CardDescription>{t('tenant.export.requestDescription')}</CardDescription>
          </div>
          <Button onClick={() => setShowExport(true)}>{t('tenant.export.requestButton')}</Button>
        </CardHeader>
      </Card>

      <ExportTenantDialog
        open={showExport}
        onClose={() => setShowExport(false)}
        onSubmit={handleRequestExport}
      />

      {/* Export History */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tenant.export.historyTitle')}</CardTitle>
          <CardDescription>{t('tenant.export.historyDescription')}</CardDescription>
        </CardHeader>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('tenant.export.columnId')}</TableHead>
              <TableHead>{t('tenant.export.columnFormat')}</TableHead>
              <TableHead>{t('tenant.export.columnScopes')}</TableHead>
              <TableHead>{t('tenant.export.columnStatus')}</TableHead>
              <TableHead>{t('tenant.export.columnSize')}</TableHead>
              <TableHead>{t('tenant.export.columnRequestedAt')}</TableHead>
              <TableHead className="text-right">{t('tenant.export.columnActions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  {t('common.loading')}
                </TableCell>
              </TableRow>
            ) : exports.length === 0 ? (
              <TableEmpty colSpan={7} message={t('tenant.export.emptyHistory')} />
            ) : (
              exports.map((exp: any) => {
                const exportId = exp.entity?.id ?? exp.id ?? '';
                const expStatus = exp.status ?? 'PENDING';
                const isCompleted = expStatus === 'COMPLETED';
                const isProcessing = expStatus === 'PROCESSING';

                return (
                  <TableRow key={exportId}>
                    <TableCell>
                      <span className="text-sm font-mono text-foreground" title={exportId}>
                        {truncateId(exportId)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" size="sm">
                        {exp.format ?? '--'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(exp.scopes ?? []).map((scope: string) => (
                          <Badge key={scope} variant="secondary" size="sm">
                            {scope}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        {isProcessing && (
                          <svg
                            className="w-3.5 h-3.5 animate-spin text-blue-500"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                        )}
                        <Badge variant={statusVariant(expStatus)} size="sm">
                          {statusLabel(expStatus, t)}
                        </Badge>
                      </div>
                      {expStatus === 'FAILED' && exp.error_message && (
                        <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                          {exp.error_message}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-foreground">
                        {formatBytes(exp.file_size_bytes)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {formatDate(exp.requested_at)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {isCompleted && exp.download_url ? (
                        <a
                          href={exp.download_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                        >
                          <Button variant="secondary" size="sm">
                            <span className="flex items-center gap-1.5">
                              <svg
                                className="w-3.5 h-3.5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                                />
                              </svg>
                              {t('tenant.export.download')}
                            </span>
                          </Button>
                        </a>
                      ) : (
                        <Button variant="ghost" size="sm" disabled>
                          {t('tenant.export.download')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
});

export default TenantDataExportPage;
